import {Injectable} from '@angular/core';
import {firstValueFrom, map} from 'rxjs';
import {GetService} from "@shared/api/services/get.service";
import {OtpModeHistoryEntry} from "@shared/api/models/query/otp-mode-history.model";
import {SetStaticOtpMode} from "@shared/api/models/command/otp-mode.model";

/**
 * UNIVAPLAT-2990: integration layer for the admin OTP mode endpoints
 * (`/api/v2/admin/users/{userId}/otp-mode*`, backend contract UNIVAPLAT-2977).
 *
 * All methods are Promise-based (firstValueFrom) per the codebase convention —
 * components must call these, never HttpClient directly. Error mapping is
 * left to the callers' catch blocks — the backend owns the user-facing error
 * text.
 *
 * Security constraint (tech-lead assessment): the OTP mode history payload
 * must not be cached in a service-level store that survives navigation —
 * `listHistory` returns it directly to the caller; the calling modal is
 * responsible for holding it for the lifetime of its own view.
 */
@Injectable({
    providedIn: 'root'
})
export class OtpModeService extends GetService<OtpModeHistoryEntry, OtpModeHistoryEntry> {

    protected override get domainEntity() {
        return 'admin/users'
    };

    /**
     * `POST /api/v2/admin/users/{userId}/otp-mode/static`
     * Pins the user's OTP to a fixed code, with a recorded reason.
     */
    async setStatic(userId: number, code: string, reason: string): Promise<{ id: number }> {
        const body: SetStaticOtpMode = {code, reason};
        const result = this.http.post<{ data: { id: number } }>(`${this.urlToEntity}/${userId}/otp-mode/static`, body)
            .pipe(
                map((response) => {
                    return response.data;
                })
            )
        return firstValueFrom(result)
    }

    /**
     * `POST /api/v2/admin/users/{userId}/otp-mode/dynamic`
     * Reverts the user's OTP mode back to dynamic (backend-generated) codes.
     * No body.
     */
    async setDynamic(userId: number): Promise<{ id: number }> {
        const result = this.http.post<{ data: { id: number } }>(`${this.urlToEntity}/${userId}/otp-mode/dynamic`, {})
            .pipe(
                map((response) => {
                    return response.data;
                })
            )
        return firstValueFrom(result)
    }

    /**
     * `GET /api/v2/admin/users/{userId}/otp-mode/history`
     * Full OTP mode change history for the user, newest-first as returned
     * by the API. Not cached at the service level — see the class-level
     * security note.
     */
    async listHistory(userId: number): Promise<OtpModeHistoryEntry[]> {
        const result = this.http.get<{ data: OtpModeHistoryEntry[] }>(`${this.urlToEntity}/${userId}/otp-mode/history`)
            .pipe(
                map((response) => {
                    return response?.data ?? [];
                })
            )
        return firstValueFrom(result)
    }
}
